import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, Search, X, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { countryMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { CountryListItem, CreateCountryRequest, UpdateCountryRequest } from '@/types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_STATUSES = '__all__'

interface CountryFilters {
  searchTerm: string
  status: string
}

export default function CountriesPage() {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingCountry, setEditingCountry] = useState<CountryListItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filters, setFilters] = useState<CountryFilters>({
    searchTerm: '',
    status: ALL_STATUSES,
  })
  const [formData, setFormData] = useState<CreateCountryRequest>({
    country_code: '',
    country_name: '',
    phone_code: '',
    currency: '',
  })

  const queryClient = useQueryClient()

  const { data: countries = [], isLoading } = useQuery({
    queryKey: ['admin-countries'],
    queryFn: () => countryMasterApi.getAllAdmin(),
  })

  // Filter countries based on filters
  const filteredCountries = useMemo(() => {
    return countries.filter(country => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        const matchesName = country.country_name.toLowerCase().includes(searchLower)
        const matchesCode = country.country_code.toLowerCase().includes(searchLower)
        if (!matchesName && !matchesCode) return false
      }
      if (filters.status !== ALL_STATUSES) {
        const isActive = filters.status === 'active'
        if (country.is_active !== isActive) return false
      }
      return true
    })
  }, [countries, filters])

  // Pagination calculations
  const totalRecords = filteredCountries.length
  const totalPages = Math.ceil(totalRecords / pageSize)
  const paginatedCountries = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredCountries.slice(startIndex, startIndex + pageSize)
  }, [filteredCountries, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const clearFilters = () => {
    setFilters({ searchTerm: '', status: ALL_STATUSES })
    setCurrentPage(1)
  }

  const hasActiveFilters = !!(filters.searchTerm || filters.status !== ALL_STATUSES)

  const createMutation = useMutation({
    mutationFn: countryMasterApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-countries'] })
      queryClient.invalidateQueries({ queryKey: ['countries'] })
      setIsDialogOpen(false)
      resetForm()
      toast.success(t('masters.country.countryCreated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.country.failedToCreate')
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCountryRequest }) =>
      countryMasterApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-countries'] })
      queryClient.invalidateQueries({ queryKey: ['countries'] })
      setIsDialogOpen(false)
      setEditingCountry(null)
      resetForm()
      toast.success(t('masters.country.countryUpdated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.country.failedToUpdate')
      toast.error(errorMessage)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: countryMasterApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-countries'] })
      queryClient.invalidateQueries({ queryKey: ['countries'] })
      toast.success(t('masters.country.countryDeleted'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.country.failedToDelete')
      toast.error(errorMessage)
    },
  })

  const resetForm = () => {
    setFormData({ country_code: '', country_name: '', phone_code: '', currency: '' })
    setEditingCountry(null)
  }

  const handleEdit = (country: CountryListItem) => {
    setEditingCountry(country)
    setFormData({
      country_code: country.country_code,
      country_name: country.country_name,
      phone_code: country.phone_code || '',
      currency: country.currency || '',
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingCountry) {
      updateMutation.mutate({ id: editingCountry.country_id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open)
    if (!open) resetForm()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('masters.country.title')}</h1>
          <p className="text-muted-foreground">{t('masters.country.subtitle')}</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t('masters.country.addCountry')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{editingCountry ? t('masters.country.editCountry') : t('masters.country.addCountry')}</DialogTitle>
                <DialogDescription>
                  {editingCountry ? t('masters.country.updateCountryConfig') : t('masters.country.createNewCountryConfig')}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="country_code">{t('masters.country.countryCode')} *</Label>
                  <Input
                    id="country_code"
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., US, IN, GB"
                    maxLength={3}
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="country_name">{t('masters.country.countryName')} *</Label>
                  <Input
                    id="country_name"
                    value={formData.country_name}
                    onChange={(e) => setFormData({ ...formData, country_name: e.target.value })}
                    placeholder="e.g., United States, India"
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="phone_code">{t('masters.country.phoneCode')}</Label>
                  <Input
                    id="phone_code"
                    value={formData.phone_code || ''}
                    onChange={(e) => setFormData({ ...formData, phone_code: e.target.value })}
                    placeholder="e.g., +1, +91"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="currency">{t('masters.country.currency')}</Label>
                  <Input
                    id="currency"
                    value={formData.currency || ''}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value.toUpperCase() })}
                    placeholder="e.g., USD, INR"
                    maxLength={3}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending
                    ? t('common.saving')
                    : editingCountry
                    ? t('masters.country.updateCountry')
                    : t('masters.country.createCountry')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            placeholder={t('masters.country.searchPlaceholder')}
            value={filters.searchTerm}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value })
              setCurrentPage(1)
            }}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.status}
          onValueChange={(value) => {
            setFilters({ ...filters, status: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('common.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('common.allStatuses')}</SelectItem>
            <SelectItem value="active">{t('common.active')}</SelectItem>
            <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      <CountryTable
        countries={paginatedCountries}
        allCountries={countries}
        filteredCountries={filteredCountries}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={(id) => deleteMutation.mutate(id)}
        isDeleting={deleteMutation.isPending}
        clearFilters={clearFilters}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRecords={totalRecords}
        totalPages={totalPages}
        startRecord={startRecord}
        endRecord={endRecord}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
        t={t}
      />
    </div>
  )
}


function CountryTable({
  countries,
  allCountries,
  filteredCountries,
  isLoading,
  onEdit,
  onDelete,
  isDeleting,
  clearFilters,
  pageSize,
  currentPage,
  totalRecords,
  totalPages,
  startRecord,
  endRecord,
  onPageSizeChange,
  onNextPage,
  onPreviousPage,
  t,
}: {
  countries: CountryListItem[]
  allCountries: CountryListItem[]
  filteredCountries: CountryListItem[]
  isLoading: boolean
  onEdit: (country: CountryListItem) => void
  onDelete: (id: string) => void
  isDeleting: boolean
  clearFilters: () => void
  pageSize: number
  currentPage: number
  totalRecords: number
  totalPages: number
  startRecord: number
  endRecord: number
  onPageSizeChange: (size: string) => void
  onNextPage: () => void
  onPreviousPage: () => void
  t: (key: string) => string
}) {
  if (isLoading) {
    return <div className="text-center py-8">{t('common.loading')}</div>
  }

  if (allCountries.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('masters.country.noCountriesFound')}
        </CardContent>
      </Card>
    )
  }

  if (filteredCountries.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="text-muted-foreground mb-4">
            {t('masters.country.noMatchingCountries')}
          </div>
          <Button variant="outline" onClick={clearFilters}>
            <X className="mr-2 h-4 w-4" />
            {t('common.reset')} {t('common.filters')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-280px)]">
      <Card className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>{t('masters.country.countryCode')}</TableHead>
                <TableHead>{t('masters.country.countryName')}</TableHead>
                <TableHead>{t('masters.country.phoneCode')}</TableHead>
                <TableHead>{t('masters.country.currency')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead className="w-[120px]">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {countries.map((country) => (
                <TableRow key={country.country_id}>
                  <TableCell className="font-medium">{country.country_code}</TableCell>
                  <TableCell>{country.country_name}</TableCell>
                  <TableCell>{country.phone_code || '-'}</TableCell>
                  <TableCell>{country.currency || '-'}</TableCell>
                  <TableCell>
                    {country.is_active ? (
                      <Badge variant="default">{t('common.active')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('common.inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(country)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          {t('common.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDelete(country.country_id)}
                          disabled={isDeleting}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination Controls */}
        <div className="flex items-center gap-3 px-4 py-2 border-t">
          <Select value={String(pageSize)} onValueChange={onPageSizeChange}>
            <SelectTrigger className="w-[70px] h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {startRecord}-{endRecord} {t('common.of')} {totalRecords}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onPreviousPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onNextPage}
              disabled={currentPage >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

